import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { useAuth } from '../auth/AuthContext'
import { uploadBotImage } from '../lib/uploadBotImage'
import { OrganizationMembersSection } from '../components/organization/OrganizationMembersSection'
import { OrganizationInfoSection } from '../components/organization/OrganizationInfoSection'
import { UsageDashboardSection } from '../components/organization/UsageDashboardSection'
import { PlanBillingSection } from '../components/organization/PlanBillingSection'
import { ProfileSection } from '../components/settings/ProfileSection'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'
const WEBHOOK_BASE = 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

function XIcon() {
  return (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function CopyIcon() {
  return (
    <svg fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  )
}

interface CirclebackConfig {
  enabled: boolean
  webhookPath?: string
  secret?: string
  signingSecretSet?: boolean
  folderId?: string | null
}

function CirclebackIntegrationCard() {
  const { user, firebaseUser } = useAuth()
  const isAdmin = user?.role === 'admin'

  const [config, setConfig] = useState<CirclebackConfig | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<'url' | 'secret' | null>(null)
  const [showSecret, setShowSecret] = useState(false)

  const fetchConfig = useCallback(async () => {
    if (!isAdmin || !firebaseUser) return
    setLoading(true)
    setError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/integrations/circleback`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!res.ok) throw new Error(`設定の取得に失敗しました (${res.status})`)
      setConfig(await res.json())
    } catch (e) {
      setError(e instanceof Error ? e.message : '設定の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [isAdmin, firebaseUser])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  const [signingSecretInput, setSigningSecretInput] = useState('')

  const postConfig = async (body: { rotateSecret?: boolean; signingSecret?: string }) => {
    if (!firebaseUser) return
    setSaving(true)
    setError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/integrations/circleback`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })
      if (!res.ok) throw new Error(`設定の保存に失敗しました (${res.status})`)
      const data = await res.json()
      setConfig({
        enabled: data.enabled,
        webhookPath: data.webhookPath,
        secret: data.secret,
        signingSecretSet: data.signingSecretSet ?? Boolean(body.signingSecret),
        folderId: data.folderId
      })
      if (body.signingSecret) setSigningSecretInput('')
    } catch (e) {
      setError(e instanceof Error ? e.message : '設定の保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleCopy = async (text: string, target: 'url' | 'secret') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(target)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      // clipboard unavailable
    }
  }

  const webhookUrl = config?.webhookPath ? `${WEBHOOK_BASE}${config.webhookPath}` : ''

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="text-lg font-semibold">Circleback連携</h2>
          <p className="form-section-desc">Circlebackの議事録をWebhook経由で自動取り込みします。</p>
        </div>
        {config && (
          config.enabled
            ? <span className="badge badge-success">有効</span>
            : <span className="badge badge-neutral">無効</span>
        )}
      </div>

      {!isAdmin ? (
        <div className="alert info">管理者のみ設定できます</div>
      ) : (
        <div className="flex flex-col gap-4">
          {error && <div className="alert danger">{error}</div>}

          {loading ? (
            <div className="flex items-center gap-2 py-4">
              <span className="spinner" />
              <span className="text-secondary text-sm">読み込み中...</span>
            </div>
          ) : (
            <>
              {config?.enabled && webhookUrl && (
                <div className="form-group">
                  <label className="form-label">Webhook URL</label>
                  <div className="flex items-center gap-2">
                    <input type="text" readOnly value={webhookUrl} className="form-input" />
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => handleCopy(webhookUrl, 'url')}
                    >
                      <CopyIcon />
                      {copied === 'url' ? 'コピーしました' : 'コピー'}
                    </button>
                  </div>
                  <p className="form-hint">CirclebackのWebhook設定にこのURLを登録してください。</p>
                </div>
              )}

              {config?.enabled && config.secret && (
                <div className="form-group">
                  <label className="form-label">シークレット</label>
                  <div className="flex items-center gap-2">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      readOnly
                      value={config.secret}
                      className="form-input"
                    />
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => setShowSecret((v) => !v)}
                    >
                      {showSecret ? '隠す' : '表示'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => handleCopy(config.secret ?? '', 'secret')}
                    >
                      <CopyIcon />
                      {copied === 'secret' ? 'コピーしました' : 'コピー'}
                    </button>
                  </div>
                  <p className="form-hint">
                    Circleback側にヘッダー設定欄が無い場合は、Webhook URLの末尾に ?token=（このシークレット） を付けて登録してください。
                    下の「Circleback署名シークレット」を設定した場合はこのトークンは不要です。
                  </p>
                </div>
              )}

              {config?.enabled && (
                <div className="form-group">
                  <label className="form-label">
                    Circleback署名シークレット
                    {config.signingSecretSet && <span className="badge badge-success" style={{ marginLeft: 8 }}>設定済み</span>}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="password"
                      className="form-input"
                      placeholder="whsec_..."
                      value={signingSecretInput}
                      onChange={(e) => setSigningSecretInput(e.target.value)}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={saving || !signingSecretInput.trim()}
                      onClick={() => postConfig({ signingSecret: signingSecretInput.trim() })}
                    >
                      保存
                    </button>
                  </div>
                  <p className="form-hint">
                    CirclebackのWebhook設定画面の「Copy secret」でコピーした署名シークレットを貼り付けてください。
                    設定すると署名検証（推奨）でリクエストの正当性を確認します。
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                {!config?.enabled && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={saving}
                    onClick={() => postConfig({})}
                  >
                    {saving && <span className="spinner sm" />}
                    連携を有効化
                  </button>
                )}
                {config?.enabled && (
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={saving}
                    onClick={() => {
                      if (window.confirm('シークレットを再発行しますか？既存のWebhook設定は無効になります。')) {
                        postConfig({ rotateSecret: true })
                      }
                    }}
                  >
                    {saving && <span className="spinner sm" />}
                    シークレットを再発行
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

interface NotificationsConfig {
  configured: boolean
  slackWebhookUrl: string | null
  emailEnabled: boolean
  emailRecipients: string[]
  notifyOnFormatted: boolean
}

interface NotificationTestResult {
  slack?: { success: boolean; error?: string }
  email?: { success: boolean; error?: string }
}

function NotificationsIntegrationCard() {
  const { user, firebaseUser } = useAuth()
  const isAdmin = user?.role === 'admin'

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<NotificationTestResult | null>(null)
  const [configured, setConfigured] = useState(false)

  const [slackWebhookUrl, setSlackWebhookUrl] = useState('')
  const [emailEnabled, setEmailEnabled] = useState(false)
  const [emailRecipients, setEmailRecipients] = useState('')
  const [notifyOnFormatted, setNotifyOnFormatted] = useState(true)

  const authFetch = useCallback(async (path: string, init?: RequestInit) => {
    if (!firebaseUser) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    })
  }, [firebaseUser])

  useEffect(() => {
    if (!isAdmin || !firebaseUser) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await authFetch('/v1/integrations/notifications')
        if (!res.ok) throw new Error(`設定の取得に失敗しました (${res.status})`)
        const data: NotificationsConfig = await res.json()
        if (cancelled) return
        setConfigured(data.configured)
        setSlackWebhookUrl(data.slackWebhookUrl || '')
        setEmailEnabled(data.emailEnabled === true)
        setEmailRecipients((data.emailRecipients || []).join(', '))
        setNotifyOnFormatted(data.notifyOnFormatted !== false)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : '設定の取得に失敗しました')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [isAdmin, firebaseUser, authFetch])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    setTestResult(null)
    try {
      const trimmedUrl = slackWebhookUrl.trim()
      if (trimmedUrl && !trimmedUrl.startsWith('https://hooks.slack.com/')) {
        throw new Error('Slack Webhook URLは https://hooks.slack.com/ で始まるURLを指定してください')
      }
      const recipients = emailRecipients
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean)
      const res = await authFetch('/v1/integrations/notifications', {
        method: 'POST',
        body: JSON.stringify({
          slackWebhookUrl: trimmedUrl,
          emailEnabled,
          emailRecipients: recipients,
          notifyOnFormatted
        })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `設定の保存に失敗しました (${res.status})`)
      setConfigured(true)
      setSlackWebhookUrl(data?.slackWebhookUrl || '')
      setEmailEnabled(data?.emailEnabled === true)
      setEmailRecipients((data?.emailRecipients || []).join(', '))
      setNotifyOnFormatted(data?.notifyOnFormatted !== false)
      setMessage('通知設定を保存しました')
      setTimeout(() => setMessage(null), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : '設定の保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    setError(null)
    setMessage(null)
    setTestResult(null)
    try {
      const res = await authFetch('/v1/integrations/notifications/test', { method: 'POST' })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `テスト送信に失敗しました (${res.status})`)
      setTestResult(data?.result || {})
    } catch (e) {
      setError(e instanceof Error ? e.message : 'テスト送信に失敗しました')
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="text-lg font-semibold">通知</h2>
          <p className="form-section-desc">議事録の完成をSlackやメールで通知します。</p>
        </div>
        {!loading && (
          configured
            ? <span className="badge badge-success">設定済み</span>
            : <span className="badge badge-neutral">未設定</span>
        )}
      </div>

      {!isAdmin ? (
        <div className="alert info">管理者のみ設定できます</div>
      ) : (
        <div className="flex flex-col gap-4">
          {error && <div className="alert danger">{error}</div>}
          {message && <div className="alert success">{message}</div>}
          {testResult && (
            <div className="alert info">
              <div className="flex flex-col gap-1">
                <span className="font-medium">テスト送信結果</span>
                {!testResult.slack && !testResult.email && (
                  <span className="text-sm">送信先が設定されていません。</span>
                )}
                {testResult.slack && (
                  <span className="text-sm">
                    Slack: {testResult.slack.success ? '✅ 送信成功' : `❌ 失敗（${testResult.slack.error || '不明なエラー'}）`}
                  </span>
                )}
                {testResult.email && (
                  <span className="text-sm">
                    メール: {testResult.email.success ? '✅ 送信成功' : `❌ 失敗（${testResult.email.error || '不明なエラー'}）`}
                  </span>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 py-4">
              <span className="spinner" />
              <span className="text-secondary text-sm">読み込み中...</span>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Slack Webhook URL（任意）</label>
                <input
                  type="url"
                  value={slackWebhookUrl}
                  onChange={(e) => setSlackWebhookUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/..."
                  className="form-input"
                />
                <p className="form-hint">SlackのIncoming Webhook URLを指定すると、指定チャンネルに通知が届きます。</p>
              </div>

              <div className="form-group">
                <label className="toggle-wrapper">
                  <span className="toggle">
                    <input
                      type="checkbox"
                      checked={emailEnabled}
                      onChange={(e) => setEmailEnabled(e.target.checked)}
                    />
                    <span className="toggle-track"></span>
                    <span className="toggle-thumb"></span>
                  </span>
                  <span className="toggle-label">メール通知</span>
                </label>
                {emailEnabled && (
                  <div style={{ marginTop: 8 }}>
                    <input
                      type="text"
                      value={emailRecipients}
                      onChange={(e) => setEmailRecipients(e.target.value)}
                      placeholder="alice@example.com, bob@example.com"
                      className="form-input"
                    />
                    <p className="form-hint">通知先のメールアドレスをカンマ区切りで指定してください。</p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="toggle-wrapper">
                  <span className="toggle">
                    <input
                      type="checkbox"
                      checked={notifyOnFormatted}
                      onChange={(e) => setNotifyOnFormatted(e.target.checked)}
                    />
                    <span className="toggle-track"></span>
                    <span className="toggle-thumb"></span>
                  </span>
                  <span className="toggle-label">議事録完成時に通知</span>
                </label>
                <p className="form-hint">AI整形が完了したタイミングで通知を送信します。</p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {saving && <span className="spinner sm" />}
                  保存
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={testing || !configured}
                  onClick={handleTest}
                >
                  {testing && <span className="spinner sm" />}
                  テスト送信
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

interface CalendarUpcomingEvent {
  eventId: string
  seriesId: string | null
  title: string
  startTime: string | null
  endTime: string | null
  meetingUrl: string | null
  botScheduled: boolean
}

interface CalendarStatus {
  connected: boolean
  calendarId?: string
  accountEmail?: string | null
  autoRecord?: boolean
  upcoming: CalendarUpcomingEvent[]
}

function MeetingBaasCalendarCard() {
  const { user, firebaseUser } = useAuth()
  const isAdmin = user?.role === 'admin'

  const [status, setStatus] = useState<CalendarStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [togglingAuto, setTogglingAuto] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ボット表示設定
  const [botName, setBotName] = useState('')
  const [botImageUrl, setBotImageUrl] = useState('')
  const [uploadingBotImage, setUploadingBotImage] = useState(false)
  const handleBotImageFile = async (file: File | undefined) => {
    if (!file || !firebaseUser) return
    setUploadingBotImage(true)
    setError(null)
    try {
      const tokenResult = await firebaseUser.getIdTokenResult()
      const url = await uploadBotImage(file, String(tokenResult.claims.orgId))
      setBotImageUrl(url)
    } catch (e) {
      setError(e instanceof Error ? e.message : '画像のアップロードに失敗しました')
    } finally {
      setUploadingBotImage(false)
    }
  }
  const [botSaving, setBotSaving] = useState(false)
  const [botMessage, setBotMessage] = useState<string | null>(null)

  // 文字起こし後のAI自動整形
  const [autoFormat, setAutoFormat] = useState(true)
  const [togglingAutoFormat, setTogglingAutoFormat] = useState(false)

  const authFetch = useCallback(async (path: string, init?: RequestInit) => {
    if (!firebaseUser) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    })
  }, [firebaseUser])

  const fetchStatus = useCallback(async () => {
    if (!firebaseUser) return
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch('/v1/meeting-bots/calendar/status')
      if (!res.ok) throw new Error(`接続状態の取得に失敗しました (${res.status})`)
      setStatus(await res.json())
    } catch (e) {
      setError(e instanceof Error ? e.message : '接続状態の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [firebaseUser, authFetch])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  // ボット表示設定の取得
  useEffect(() => {
    if (!isAdmin || !firebaseUser) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch('/v1/integrations/meeting-bots')
        if (!res.ok) return
        const data = await res.json()
        if (cancelled) return
        setBotName(data.botName || '')
        setBotImageUrl(data.botImageUrl || '')
        setAutoFormat(data.autoFormat !== false)
      } catch {
        // ベストエフォート
      }
    })()
    return () => { cancelled = true }
  }, [isAdmin, firebaseUser, authFetch])

  const handleSaveBotSettings = async () => {
    setBotSaving(true)
    setBotMessage(null)
    setError(null)
    try {
      const trimmedImage = botImageUrl.trim()
      if (trimmedImage && !/^https:\/\//i.test(trimmedImage)) {
        throw new Error('ボット画像URLは https:// で始まるURLを指定してください')
      }
      const res = await authFetch('/v1/integrations/meeting-bots', {
        method: 'POST',
        body: JSON.stringify({ botName: botName.trim(), botImageUrl: trimmedImage })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `ボット設定の保存に失敗しました (${res.status})`)
      setBotName(data?.botName || '')
      setBotImageUrl(data?.botImageUrl || '')
      setBotMessage('ボット設定を保存しました')
      setTimeout(() => setBotMessage(null), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ボット設定の保存に失敗しました')
    } finally {
      setBotSaving(false)
    }
  }

  const handleAutoFormatToggle = async (next: boolean) => {
    setTogglingAutoFormat(true)
    setError(null)
    const prev = autoFormat
    setAutoFormat(next)
    try {
      const res = await authFetch('/v1/integrations/meeting-bots', {
        method: 'POST',
        body: JSON.stringify({ autoFormat: next })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `設定の変更に失敗しました (${res.status})`)
      setAutoFormat(data?.autoFormat !== false)
    } catch (e) {
      setAutoFormat(prev)
      setError(e instanceof Error ? e.message : '設定の変更に失敗しました')
    } finally {
      setTogglingAutoFormat(false)
    }
  }

  const handleConnect = async () => {
    setConnecting(true)
    setError(null)
    try {
      const res = await authFetch('/v1/meeting-bots/calendar/oauth/start')
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `連携の開始に失敗しました (${res.status})`)
      if (!data?.authUrl) throw new Error('認証URLの取得に失敗しました')
      window.location.href = data.authUrl
    } catch (e) {
      setError(e instanceof Error ? e.message : '連携の開始に失敗しました')
      setConnecting(false)
    }
  }

  const handleAutoRecordToggle = async (next: boolean) => {
    setTogglingAuto(true)
    setError(null)
    try {
      const res = await authFetch('/v1/meeting-bots/calendar', {
        method: 'PATCH',
        body: JSON.stringify({ autoRecord: next })
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.error || `設定の変更に失敗しました (${res.status})`)
      }
      setStatus((s) => (s ? { ...s, autoRecord: next } : s))
    } catch (e) {
      setError(e instanceof Error ? e.message : '設定の変更に失敗しました')
    } finally {
      setTogglingAuto(false)
    }
  }


  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="text-lg font-semibold">MeetingBaaS カレンダー連携</h2>
          <p className="form-section-desc">Googleカレンダーを接続すると、会議に議事録ボットを自動派遣できます。</p>
        </div>
        {status && (
          status.connected
            ? <span className="badge badge-success">接続済み</span>
            : <span className="badge badge-neutral">未接続</span>
        )}
      </div>

      {(
        <div className="flex flex-col gap-4">
          {error && <div className="alert danger">{error}</div>}

          {botMessage && <div className="alert success">{botMessage}</div>}

          {isAdmin && (
          <div className="form-section">
            <h3 className="form-section-title">ボット表示設定</h3>
            <p className="form-section-desc">会議に参加するボットの表示名とアバター画像を設定します。</p>
            <div className="form-group">
              <label className="form-label">ボット表示名</label>
              <input
                type="text"
                value={botName}
                onChange={(e) => setBotName(e.target.value)}
                placeholder="Bluepaper 議事録ボット"
                className="form-input"
              />
              <p className="form-hint">未入力の場合は「Bluepaper 議事録ボット」が使用されます。</p>
            </div>
            <div className="form-group">
              <label className="form-label">ボット画像URL（任意）</label>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={botImageUrl}
                  onChange={(e) => setBotImageUrl(e.target.value)}
                  placeholder="https://example.com/bot-avatar.png"
                  className="form-input flex-1"
                />
                <label className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  {uploadingBotImage ? 'アップロード中...' : '画像をアップロード'}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    disabled={uploadingBotImage}
                    onChange={(e) => { handleBotImageFile(e.target.files?.[0]); e.target.value = '' }}
                  />
                </label>
                {botImageUrl.trim().startsWith('https://') && (
                  <img
                    src={botImageUrl.trim()}
                    alt="ボット画像プレビュー"
                    style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden' }}
                    onLoad={(e) => { (e.target as HTMLImageElement).style.visibility = 'visible' }}
                  />
                )}
              </div>
              <p className="form-hint">画像ファイルをアップロードするか、https:// で始まる画像URLを指定してください（JPEG / PNG / WebP、5MBまで）。</p>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={botSaving}
                onClick={handleSaveBotSettings}
              >
                {botSaving && <span className="spinner sm" />}
                保存
              </button>
            </div>
            <div className="form-group">
              <label className="toggle-wrapper">
                <span className="toggle">
                  <input
                    type="checkbox"
                    checked={autoFormat}
                    disabled={togglingAutoFormat}
                    onChange={(e) => handleAutoFormatToggle(e.target.checked)}
                  />
                  <span className="toggle-track"></span>
                  <span className="toggle-thumb"></span>
                </span>
                <span className="toggle-label">文字起こし後にAI整形を自動実行</span>
              </label>
              <p className="form-hint">有効にすると、ボットの文字起こし完了後に議事録のAI整形を自動で実行します。</p>
            </div>
          </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 py-4">
              <span className="spinner" />
              <span className="text-secondary text-sm">読み込み中...</span>
            </div>
          ) : !status?.connected ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn btn-primary"
                disabled={connecting}
                onClick={handleConnect}
              >
                {connecting && <span className="spinner sm" />}
                Googleカレンダーを接続
              </button>
              <p className="form-hint">Googleアカウントの認証画面に移動します。</p>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">接続中のカレンダー</label>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm">
                    {status.accountEmail || status.calendarId}
                  </p>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={handleConnect}
                    disabled={connecting}
                    title="権限スコープの追加（Gmail連携など）にはGoogle認証のやり直しが必要です"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {connecting ? '接続中...' : '再接続'}
                  </button>
                </div>
                <p className="form-hint">メール履歴の表示（Gmail読み取り）を有効にするには再接続が必要です。</p>
              </div>

              {isAdmin ? (
              <div>
                <label className="toggle-wrapper">
                  <span className="toggle">
                    <input
                      type="checkbox"
                      checked={status.autoRecord !== false}
                      disabled={togglingAuto}
                      onChange={(e) => handleAutoRecordToggle(e.target.checked)}
                    />
                    <span className="toggle-track"></span>
                    <span className="toggle-thumb"></span>
                  </span>
                  <span className="toggle-label">新しい会議を自動で記録する（組織全体）</span>
                </label>
                <p className="form-hint">有効にすると、会議URL付きの新規カレンダー予定に議事録ボットを自動予約します。</p>
              </div>
              ) : (
                <p className="form-hint">組織全体の自動記録設定は管理者のみ変更できます。</p>
              )}

              {/* 個別会議の記録設定は「会議予定」ページに集約（設定画面からは撤去） */}
              <p className="form-hint">
                個別の会議ごとの記録ON/OFFは<a href="/" style={{ marginLeft: 4 }}>会議予定ページ</a>から設定できます。
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// 共有設定（組織のデフォルト公開範囲）カード
function SharingSettingsCard() {
  const { user, firebaseUser } = useAuth()
  const [defaultVisibility, setDefaultVisibility] = useState<'private' | 'folder' | 'org'>('folder')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        if (!firebaseUser || !user?.orgId) return
        const token = await firebaseUser.getIdToken()
        const res = await fetch(`${API_BASE}/v1/organizations/${user.orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (!res.ok) throw new Error('組織情報の取得に失敗しました')
        const data = await res.json()
        const v = data?.settings?.defaultVisibility
        if (v === 'private' || v === 'folder' || v === 'org') {
          setDefaultVisibility(v)
        }
      } catch (error) {
        console.error('Failed to load org settings:', error)
      } finally {
        setLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser, user?.orgId])

  const handleSave = async () => {
    try {
      if (!firebaseUser || !user?.orgId) return
      setSaving(true)
      setMessage(null)
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/organizations/${user.orgId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ settings: { defaultVisibility } })
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.error || '保存に失敗しました')
      }
      setMessage('共有設定を保存しました')
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      alert(error instanceof Error ? error.message : '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const options: { value: 'private' | 'folder' | 'org'; title: string; desc: string }[] = [
    { value: 'private', title: '自分のみ', desc: '新しい議事録は作成者だけが閲覧できます。共有したい場合は議事録ごとに公開範囲を変更します。' },
    { value: 'folder', title: 'フォルダ内', desc: '新しい議事録は、その議事録が入っているフォルダにアクセスできるメンバーが閲覧できます。フォルダ未設定の場合は作成者のみです。' },
    { value: 'org', title: '組織全体', desc: '新しい議事録は組織のメンバー全員が閲覧できます。' }
  ]

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="text-lg font-semibold">共有設定</h2>
          <p className="form-section-desc">新しく作成される議事録のデフォルトの公開範囲を設定します（管理者のみ変更可能）。</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-4">
          <span className="spinner sm" aria-hidden="true" />
          <span className="text-sm text-secondary">読み込み中...</span>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="form-section">
            <h3 className="form-section-title">デフォルトの公開範囲</h3>
            <div className="space-y-2">
              {options.map((opt) => (
                <label
                  key={opt.value}
                  className={`stage-option flex items-center p-3 cursor-pointer${defaultVisibility === opt.value ? ' selected' : ''}`}
                  style={{ textAlign: 'left' }}
                >
                  <input
                    type="radio"
                    name="defaultVisibility"
                    value={opt.value}
                    checked={defaultVisibility === opt.value}
                    onChange={() => setDefaultVisibility(opt.value)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-primary">{opt.title}</div>
                    <div className="text-xs text-tertiary">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <p className="form-hint mt-2">
              この設定は新しく作成される議事録にのみ適用されます。既存の議事録の公開範囲は変わりません。議事録ごとの公開範囲は各議事録の「共有」から変更できます。
            </p>
          </div>

          <div className="flex justify-end items-center gap-3">
            {message && <span className="text-sm" style={{ color: 'var(--color-success, #16a34a)' }}>{message}</span>}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function SettingsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [searchParams, setSearchParams] = useSearchParams()
  const calendarParam = searchParams.get('calendar')
  const calendarReason = searchParams.get('reason')
  const tabParam = searchParams.get('tab')
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  type SettingsTab = 'profile' | 'members' | 'organization' | 'usage' | 'formatting' | 'integrations' | 'orgIntegrations' | 'sharing' | 'plan'
  const isValidTab = (t: string | null): t is SettingsTab =>
    t === 'profile' || t === 'members' || t === 'organization' || t === 'usage' || t === 'formatting' || t === 'integrations' || t === 'orgIntegrations' || t === 'sharing' || t === 'plan'
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    calendarParam ? 'integrations' : isValidTab(tabParam) ? tabParam : 'members'
  )

  // タブ変更をURL(?tab=)へ反映し、リロードしても同じタブに留まる
  const selectTab = (t: SettingsTab) => {
    setActiveTab(t)
    const next = new URLSearchParams(searchParams)
    next.set('tab', t)
    setSearchParams(next, { replace: true })
  }
  const [calendarAlert, setCalendarAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(
    calendarParam === 'connected'
      ? { type: 'success', message: 'Googleカレンダーを接続しました。' }
      : calendarParam === 'error'
        ? { type: 'danger', message: `カレンダー接続に失敗しました${calendarReason ? `（${calendarReason}）` : ''}。もう一度お試しください。` }
        : null
  )

  useEffect(() => {
    if (calendarParam) {
      // 一度表示したらURLからクエリを除去（リロードでの再表示を防ぐ）
      const next = new URLSearchParams(searchParams)
      next.delete('calendar')
      next.delete('reason')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFolderSelect = (folderId: string | null) => {
    setSelectedFolderId(folderId)
    if (folderId) {
      navigate(`/documents?folder=${folderId}`)
    } else {
      navigate('/documents')
    }
  }

  return (
    <Layout
      title="設定"
      showSidebar={true}
      selectedFolderId={selectedFolderId}
      onFolderSelect={handleFolderSelect}
    >
      <p className="form-section-desc">議事録作成とAI整形の設定を管理します</p>

      {/* タブナビゲーション（個人設定｜組織管理 の2グループ。
          グループ名はタブの上に小さく表示し、タブ本体と見た目を区別する） */}
      <div className="flex items-end gap-8 flex-wrap" style={{ borderBottom: '1px solid var(--color-border-light)', marginBottom: 'var(--space-6)' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-tertiary)', letterSpacing: '0.08em', marginBottom: 2, paddingLeft: 4 }}>個人設定</div>
          <div className="tab-list" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button
              className={`tab-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => selectTab('profile')}
            >
              プロフィール
            </button>
            <button
              className={`tab-item ${activeTab === 'integrations' ? 'active' : ''}`}
              onClick={() => selectTab('integrations')}
            >
              カレンダー連携
            </button>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-tertiary)', letterSpacing: '0.08em', marginBottom: 2, paddingLeft: 4 }}>組織管理</div>
          <div className="tab-list" style={{ marginBottom: 0, borderBottom: 'none' }}>
        <button
          className={`tab-item ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => selectTab('members')}
        >
          メンバー
        </button>
        <button
          className={`tab-item ${activeTab === 'organization' ? 'active' : ''}`}
          onClick={() => selectTab('organization')}
        >
          組織設定
        </button>
        <button
          className={`tab-item ${activeTab === 'orgIntegrations' ? 'active' : ''}`}
          onClick={() => selectTab('orgIntegrations')}
        >
          連携
        </button>
        {isAdmin && (
          <button
            className={`tab-item ${activeTab === 'sharing' ? 'active' : ''}`}
            onClick={() => selectTab('sharing')}
          >
            共有設定
          </button>
        )}
        <button
          className={`tab-item ${activeTab === 'formatting' ? 'active' : ''}`}
          onClick={() => selectTab('formatting')}
        >
          整形ルール
        </button>
        {isAdmin && (
          <button
            className={`tab-item ${activeTab === 'usage' ? 'active' : ''}`}
            onClick={() => selectTab('usage')}
          >
            利用状況
          </button>
        )}
        {isAdmin && (
          <button
            className={`tab-item ${activeTab === 'plan' ? 'active' : ''}`}
            onClick={() => selectTab('plan')}
          >
            プラン・請求
          </button>
        )}
          </div>
        </div>
      </div>

      {activeTab === 'members' && (
        <OrganizationMembersSection />
      )}

      {activeTab === 'profile' && (
        <ProfileSection />
      )}

      {activeTab === 'organization' && (
        <OrganizationInfoSection />
      )}

      {activeTab === 'orgIntegrations' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))',
            gap: 'var(--space-4, 16px)',
            alignItems: 'start'
          }}
        >
          <CirclebackIntegrationCard />
          <NotificationsIntegrationCard />
        </div>
      )}

      {activeTab === 'usage' && isAdmin && (
        <UsageDashboardSection />
      )}

      {/* 旧「コンテキスト情報管理」タブは /context（コンテキストマネジメント）ページに移行しました */}
      {activeTab === 'formatting' && (
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="text-lg font-semibold">整形ルール</h2>
              <p className="form-section-desc">AI整形時に適用するルールとテンプレートを管理します。</p>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            {/* 整形テンプレート */}
            <div className="form-section">
              <h3 className="form-section-title">整形テンプレート</h3>
              <div className="form-group">
                <label className="form-label">議事録の構成テンプレート</label>
                <textarea
                  rows={8}
                  placeholder="# 会議名&#10;&#10;## 基本情報&#10;- 日時: &#10;- 参加者: &#10;- 場所: &#10;&#10;## アジェンダ&#10;&#10;## 議論内容&#10;&#10;## 決定事項&#10;&#10;## アクションアイテム&#10;&#10;## 次回予定"
                  className="form-input font-mono"
                />
              </div>
              <div className="form-group">
                <label className="form-label">整形時の指示プロンプト</label>
                <textarea
                  rows={4}
                  placeholder="以下の音声認識結果を、読みやすい議事録形式に整形してください。重要な決定事項とアクションアイテムを明確に分けて記載してください。"
                  className="form-input"
                />
              </div>
            </div>

            {/* 用語辞書はコンテキストページへ移動 */}
            <div className="form-section">
              <h3 className="form-section-title">用語辞書</h3>
              <div className="alert info">
                <span>
                  用語辞典はコンテキストページへ移動しました。{' '}
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={{ marginLeft: 8 }}
                    onClick={() => navigate('/context?tab=terms')}
                  >
                    用語辞典を開く
                  </button>
                </span>
              </div>
            </div>

            <div className="flex justify-end">
              <button className="btn btn-primary">保存</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'integrations' && (
        <div className="flex flex-col gap-4">
          {calendarAlert && (
            <div className={`alert ${calendarAlert.type === 'success' ? 'success' : 'danger'}`}>
              <span>{calendarAlert.message}</span>
              <button
                type="button"
                className="btn-icon sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => setCalendarAlert(null)}
              >
                <XIcon />
              </button>
            </div>
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))',
              gap: 'var(--space-4, 16px)',
              alignItems: 'start'
            }}
          >
            <MeetingBaasCalendarCard />
          </div>
        </div>
      )}

      {activeTab === 'sharing' && isAdmin && (
        <SharingSettingsCard />
      )}

      {activeTab === 'plan' && isAdmin && (
        <PlanBillingSection />
      )}
    </Layout>
  )
}
